import React from "react";

/**
 * Reusable empty-state placeholder (T39 P0 #3 — the first-run a buyer judges).
 *
 * Replaces the app's ad-hoc "No Record Found" / "No Data Found.." text blocks
 * with one designed, branded state. Uses self-contained inline styles (no theme
 * dependency) so it renders consistently across the mixed MUI v4/v5 surfaces.
 *
 * Props:
 *   title       — headline (default "Nothing here yet")
 *   description — supporting line (optional)
 *   icon        — React node rendered above the title (optional; a default mark is shown)
 *   actionLabel + onAction — optional call-to-action button
 *   compact     — tighter padding for in-table / in-card use
 */
function EmptyState({
  title = "Nothing here yet",
  description,
  icon,
  actionLabel,
  onAction,
  compact = false,
}) {
  return (
    <div role="status" style={{ ...S.wrap, padding: compact ? "28px 16px" : "56px 24px" }}>
      <div style={S.iconWrap}>{icon != null ? icon : DEFAULT_ICON}</div>
      <div style={S.title}>{title}</div>
      {description ? <div style={S.desc}>{description}</div> : null}
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction} style={S.btn}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

const BRAND = "#1B3F8F";

const DEFAULT_ICON = (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 7.5 12 3l9 4.5M3 7.5 12 12m-9-4.5V16.5L12 21m0-9 9-4.5M12 12v9m9-13.5V16.5L12 21"
      stroke={BRAND}
      strokeWidth="1.4"
      strokeLinejoin="round"
      strokeLinecap="round"
      opacity="0.55"
    />
  </svg>
);

const S = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  iconWrap: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    background: "rgba(27,63,143,0.07)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  title: { fontSize: "16px", fontWeight: 600, color: "#1c2333", marginBottom: "6px" },
  desc: { fontSize: "13.5px", lineHeight: 1.5, color: "#7a8494", maxWidth: "340px" },
  btn: {
    appearance: "none",
    border: "none",
    cursor: "pointer",
    marginTop: "18px",
    background: BRAND,
    color: "#ffffff",
    fontSize: "13.5px",
    fontWeight: 600,
    padding: "9px 18px",
    borderRadius: "8px",
  },
};

export default EmptyState;
